import {
  Bell,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

const severityColorMap = {
  high: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-500" },
  medium: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-500" },
  low: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-500" },
  info: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-500" },
};

function getSeverityColor(severity) {
  return severityColorMap[severity] || severityColorMap.info;
}

export default function SmartAlertsWidget({
  darkMode,
  smartAlerts,
  alertsExpanded,
  setAlertsExpanded,
  triggerIntelligenceRun,
  navigate,
}) {
  return (
    <Collapsible open={alertsExpanded} onOpenChange={setAlertsExpanded}>
      <div
        className={`rounded-xl overflow-hidden ${
          darkMode
            ? "bg-[#161b22] border border-[#30363d]"
            : "bg-white border border-gray-100 shadow-sm"
        }`}
      >
        <CollapsibleTrigger className="w-full">
          <div
            className={`px-5 py-4 border-b flex items-center justify-between cursor-pointer ${
              darkMode ? "border-[#21262d]" : "border-gray-100"
            }`}
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#DC143C]" />
              <h3 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
                Smart Alerts
              </h3>
              {smartAlerts.length > 0 && (
                <Badge className="bg-[#DC143C] text-white border-0 text-xs">
                  {smartAlerts.length}
                </Badge>
              )}
            </div>
            {alertsExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
            {smartAlerts.length > 0 ? (
              smartAlerts.map((alert, idx) => {
                const colors = getSeverityColor(alert.severity);

                const handleAlertAction = () => {
                  const actionType = alert.action_type || alert.type;
                  if (
                    actionType === "run_scan" ||
                    alert.type === "stale_data" ||
                    alert.type === "no_data"
                  ) {
                    triggerIntelligenceRun();
                  } else if (actionType === "view_report") {
                    navigate("/intelligence");
                  } else if (alert.type === "viral_content") {
                    navigate("/social");
                  } else if (alert.type === "content_gap") {
                    navigate("/content");
                  } else if (alert.type === "competitor_activity") {
                    navigate("/competitors");
                  }
                };

                return (
                  <div
                    key={alert.id || `${alert.type}-${alert.title}-${idx}`}
                    className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}
                    data-testid={`smart-alert-${idx}`}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className={`w-4 h-4 mt-0.5 ${colors.text}`} />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {alert.title}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            darkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {alert.description}
                        </p>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={handleAlertAction}
                          className={`h-auto p-0 mt-2 text-xs ${colors.text} font-medium hover:underline`}
                          data-testid={`alert-action-${idx}`}
                        >
                          {alert.action} →
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={`text-center py-6 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No alerts at the moment</p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
